/*
  Product card script

  This file owns interactive product-card content, including swatch-driven media and link updates.
*/

class ProductCardSwatches extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.card = this.closest(".product-card");

    if (!this.card) return;

    this.form = this.card.querySelector("form[action*='/cart/add']");
    this.variantInput = this.form?.querySelector("[data-product-variant-input]");
    this.priceCurrent = this.card.querySelector(".product-card-price-current");
    this.priceCompare = this.card.querySelector(".product-card-price-compare");
    this.addToCartButton = this.form?.querySelector('[type="submit"][name="add"]');
    this.variants = this.parseVariants();

    this.listenerController = new AbortController();
    this.addEventListener("change", this.onChange.bind(this), {
      signal: this.listenerController.signal,
    });

    this.isInitialized = true;
  }

  disconnectedCallback() {
    this.listenerController?.abort();
    this.isInitialized = false;
  }

  parseVariants() {
    try {
      return JSON.parse(this.querySelector("[data-product-card-variants]")?.textContent || "[]");
    } catch {
      return [];
    }
  }

  onChange(event) {
    const swatch = event.target.closest("[data-product-card-swatch]");

    if (swatch) {
      this.updateImage(swatch);
      this.updateVariantSelection(swatch);
      this.updateLinks(swatch.dataset.variantUrl);
      return;
    }

    const option = event.target.closest("[data-product-card-option]");

    if (!option) return;

    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariant(selectedOptions);

    if (!variant) return;

    this.updateImageFromVariant(variant);
    this.updateVariantSelection(variant);
    this.updateLinks(variant.url);
  }

  getSelectedOptions() {
    return Array.from(this.querySelectorAll("[data-product-card-option]") || [])
      .map((option) => {
        const isSelect = option.matches("select");
        const value = isSelect ? option.value : option.checked ? option.value : null;

        if (!value) return null;

        return {
          position: Number(option.dataset.optionPosition),
          value,
        };
      })
      .filter(Boolean);
  }

  findVariant(selectedOptions) {
    return (
      this.variants.find((variant) => {
        return selectedOptions.every((selected) => {
          const optionIndex = selected.position - 1;
          return variant.options[optionIndex] === selected.value;
        });
      }) || null
    );
  }

  updateImage(swatch) {
    const currentImage = this.card.querySelector(".product-card-image");
    const imageTemplate = swatch
      .closest(".product-card-swatch-item")
      ?.querySelector("[data-product-card-swatch-image]");
    const nextImage = imageTemplate?.content.querySelector("img")?.cloneNode(true);

    if (!currentImage || !nextImage) return;

    nextImage.className = currentImage.className;
    currentImage.replaceWith(nextImage);
  }

  updateImageFromVariant(variant) {
    const currentImage = this.card.querySelector(".product-card-image");

    if (!currentImage || !variant.featuredMediaUrl) return;

    const nextImage = document.createElement("img");
    nextImage.src = variant.featuredMediaUrl;
    nextImage.alt = variant.featuredMediaAlt || this.card.dataset.productTitle || "";
    nextImage.className = currentImage.className;
    nextImage.loading = "lazy";

    currentImage.replaceWith(nextImage);
  }

  updateVariantSelection(variant) {
    if (this.variantInput) {
      this.variantInput.value = variant.id || this.variantInput.value;
    }

    if (this.addToCartButton) {
      this.addToCartButton.disabled = !variant.available;
      this.addToCartButton.setAttribute("aria-disabled", String(!variant.available));
    }

    if (this.priceCurrent) {
      this.priceCurrent.textContent = variant.priceMoney;
    }

    if (this.priceCompare) {
      if (variant.compareAtPrice > variant.price) {
        this.priceCompare.textContent = variant.compareAtPriceMoney;
        this.priceCompare.hidden = false;
      } else {
        this.priceCompare.textContent = "";
        this.priceCompare.hidden = true;
      }
    }
  }

  updateLinks(variantUrl) {
    if (!variantUrl) return;

    this.card.querySelectorAll("[data-product-card-link]").forEach((link) => {
      link.setAttribute("href", variantUrl);
    });
    this.card.querySelectorAll("[data-quick-add-url]").forEach((trigger) => {
      trigger.dataset.quickAddUrl = variantUrl;
    });
  }
}

if (!customElements.get("product-card-swatches")) {
  customElements.define("product-card-swatches", ProductCardSwatches);
}
